import { AlertCircle, ChevronDown } from 'lucide-react';
import { useState } from 'react';

interface FunnelStageProps {
  name: string;
  opportunities: number;
  avgValue: string;
  totalValue: string;
  probability: number;
  atRisk: number;
  riskLevel: 'low' | 'medium' | 'high';
  conversionRate: number;
  stageIndex?: number;
  totalStages?: number;
  weightedValue: string;
  avgDays: number;
  agingDeals: number;
  onStageClick: () => void;
  onAtRiskClick: () => void;
}

export function FunnelStage({
  name,
  opportunities,
  avgValue,
  totalValue,
  probability,
  atRisk,
  riskLevel,
  conversionRate,
  stageIndex = 0,
  totalStages = 4,
  weightedValue,
  avgDays,
  agingDeals,
  onStageClick,
  onAtRiskClick,
}: FunnelStageProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const getRiskColors = () => {
    switch (riskLevel) {
      case 'high':
        return { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', hover: 'hover:bg-rose-100' };
      case 'medium':
        return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', hover: 'hover:bg-amber-100' };
      case 'low':
        return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', hover: 'hover:bg-emerald-100' };
    }
  };

  // Get gradient background with progressive intensity (all same height)
  const getGradientClass = () => {
    const gradients = [
      'bg-gradient-to-br from-[#E8F4F6] to-white', // Lightest teal
      'bg-gradient-to-br from-[#D0E9ED] to-white', 
      'bg-gradient-to-br from-[#B8DEE4] to-white',
      'bg-gradient-to-br from-[#A0D3DB] to-white', // Darkest
    ];
    return gradients[stageIndex] || 'bg-white';
  };

  // Get border intensity
  const getBorderClass = () => {
    const borders = [
      'border border-gray-200',
      'border border-gray-250',
      'border-[1.5px] border-gray-300',
      'border-2 border-[#4A90A4]/30',
    ];
    return borders[stageIndex] || 'border border-gray-200';
  };

  const riskColors = getRiskColors();

  const handleCardClick = () => {
    if (isExpanded) {
      setIsExpanded(false);
    } else {
      setIsExpanded(true);
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className={`relative rounded-lg p-4 cursor-pointer hover:border-[#4A90A4] hover:shadow-md transition-all h-48 ${getBorderClass()} ${getGradientClass()} flex flex-col`}
    >
      {/* Probability Badge in corner */}
      <div className="absolute top-3 right-3 z-10">
        <div className="bg-white/80 backdrop-blur-sm text-[#4A90A4] text-[10px] px-2 py-0.5 rounded border border-[#4A90A4]/20">
          {probability}% prob
        </div>
      </div>

      {/* Collapse indicator when expanded */}
      {isExpanded && (
        <div className="absolute top-3 left-3 z-10">
          <ChevronDown className="w-4 h-4 text-[#4A90A4] rotate-180 transition-transform" />
        </div>
      )}

      <div className="flex-1 flex flex-col justify-between">
        {/* Stage Name */}
        <div className="pr-16">
          <h3 className="text-sm text-gray-900 mb-0.5 leading-tight">{name}</h3>
          <p className="text-[10px] text-gray-400">Stage</p>
        </div>
        
        {/* Metrics */}
        <div className="space-y-2">
          <div>
            <p className="text-2xl text-gray-900 leading-none">{opportunities} opps</p>
            <p className="text-xs text-gray-500 mt-0.5">opportunities</p>
          </div>
          <div>
            <p className="text-lg text-gray-900 leading-none">{totalValue} total</p>
            <p className="text-[10px] text-gray-400 mt-0.5">total pipeline</p>
          </div>
        </div>

        {/* At-Risk Badge */}
        {atRisk > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAtRiskClick();
            }}
            className={`inline-flex items-center gap-1 ${riskColors.bg} ${riskColors.border} ${riskColors.text} border text-xs px-2.5 py-1 rounded ${riskColors.hover} transition-colors self-start mt-2`}
          >
            <AlertCircle className="w-3.5 h-3.5" />
            <span>{atRisk} at risk</span>
          </button>
        )}
      </div>

      {/* Expanded Details Section - Clean popover style */}
      {isExpanded && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-4 space-y-3 z-20">
          {/* 2-Column Metrics Layout */}
          <div className="grid grid-cols-2 gap-4">
            {/* Deal Metrics Section */}
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wide text-gray-400">Deal Metrics</p>
              <div className="space-y-1.5">
                <p className="text-xs text-gray-600">
                  <span className="text-gray-500">Avg Size:</span> {avgValue}
                </p>
                <div>
                  <p className="text-xs text-gray-600">
                    <span className="text-gray-500">Weighted:</span> {weightedValue}
                  </p>
                  <p className="text-[10px] text-gray-400">(realistic forecast)</p>
                </div>
              </div>
            </div>

            {/* Velocity Section */}
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wide text-gray-400">Velocity</p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <span 
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      avgDays <= 30 ? 'bg-[#10b981]' : 
                      avgDays <= 60 ? 'bg-[#f59e0b]' : 
                      'bg-[#ef4444]'
                    }`}
                  />
                  <span>Avg Days: {avgDays}d</span>
                </div>
                {agingDeals > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-[#ef4444]">
                    <span className="w-2 h-2 rounded-full bg-[#ef4444] flex-shrink-0" />
                    <span>Aging: {agingDeals} deals &gt;60d</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onStageClick();
            }}
            className="w-full bg-[#4A90A4] text-white py-2 rounded text-xs hover:bg-[#3d7a8a] transition-colors text-center"
          >
            View All Opportunities
          </button>
        </div>
      )}
    </div>
  );
}
